import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import {
  activeColumn,
  boldLinkColumn,
  CreateNewEntityButton,
  DynamicFilters,
  Filter,
  PageComponent,
  TableComponent,
  TableProps,
  textColumn,
} from '@dispatch/web/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Actions,
  AttributeOptions,
  CompanyRole,
  MultiSelectOption,
  Resource,
  ResourceFilterGroup,
  ResourcesListFilter,
  ResultStatus,
  Subjects,
  Team,
} from '@dispatch/shared/types';
import {
  PaginatedQueryResult,
  useFilter,
  useFiltersOrderIndexBulkResourceFilterUpdate,
  useTableState,
} from '@dispatch/shared/hooks';
import { StyledResources } from './ResourcesStyle';
import { useNavigate } from 'react-router-dom';
import { Ability } from '@casl/ability';
import { Button, FormControlLabel, Switch } from '@mui/material';

/**
 * Reserved filter keys for the attribute filtering modes. The individual
 * company-attribute filters are keyed by `attributeDefinition` display name
 * (human-readable, title-cased labels), so these camelCase identifier values
 * cannot collide with a real attribute display name.
 *
 * - `ATTRIBUTE_GROUPS_FILTER_KEY` holds the dispatcher's grouped-attribute
 *   selection (a `string[]` of attribute-group IDs). It is the UI source of
 *   truth that round-trips through saved views; the API fields
 *   (`resourceAttributeGroupIds` / `requireAllAttributeGroups`) are derived
 *   from it, never written into `filters` directly.
 * - `ATTRIBUTE_MODE_KEY` persists the active mode so saved views restore in the
 *   correct mode.
 */
export const ATTRIBUTE_GROUPS_FILTER_KEY = 'attributeGroups';
export const ATTRIBUTE_MODE_KEY = 'attributeMode';

type AttributeMode = 'individual' | 'grouped';

export interface ResourcesListProps {
  data: PaginatedQueryResult<Resource[], unknown>;
  onSearch: (value: string) => void;
  onFilterChange: (filters: ResourcesListFilter) => void;
  filters: ResourcesListFilter;
  permissions: Ability<[Actions, Subjects]> | undefined;
  handleSort?: (value: GridSortModel) => void;
  companyAttributes: AttributeOptions[];
  attributeGroupOptions?: MultiSelectOption[];
  filterOptions: Record<string, MultiSelectOption[]>;
  filteredRsrcCounties: MultiSelectOption[];
  filteredRsrcCountiesStatus: ResultStatus;
  setRsrcCountyFilterInput: React.Dispatch<React.SetStateAction<string>>;
  setRsrcStateFilterInput: React.Dispatch<React.SetStateAction<string[]>>;
  storedFilters: ResourcesListFilter[];
  handleSaveFilters: (filters: ResourcesListFilter) => void;
  handleDeleteFilter: (filterId: string) => void;
  handleOrderIndexBulkUpdate: (values: ResourcesListFilter[]) => void;
  handleGroupsOrderIndexBulkUpdate: (values: ResourceFilterGroup[]) => void;
  handleSaveFilterGroup: (values: ResourceFilterGroup) => void;
  handleDeleteFilterGroup: (groupId: string) => void;
}

export function ResourcesList({
  data,
  onSearch,
  onFilterChange,
  filters: listFilters,
  permissions,
  handleSort,
  companyAttributes,
  attributeGroupOptions = [],
  filterOptions,
  filteredRsrcCounties,
  filteredRsrcCountiesStatus,
  setRsrcCountyFilterInput,
  setRsrcStateFilterInput,
  storedFilters,
  handleSaveFilters,
  handleDeleteFilter,
  handleOrderIndexBulkUpdate,
  handleGroupsOrderIndexBulkUpdate,
  handleSaveFilterGroup,
  handleDeleteFilterGroup,
}: ResourcesListProps) {
  const navigate = useNavigate();
  const { quickFilters, filters, setFilters } = useFilter();
  const { tableState, setTableState } = useTableState();

  const getModeFromFilters = (
    source: Record<string, unknown> | undefined
  ): AttributeMode => {
    if (!source) return 'individual';
    if (source[ATTRIBUTE_MODE_KEY] === 'grouped') return 'grouped';
    if (source[ATTRIBUTE_MODE_KEY] === 'individual') return 'individual';
    const groups = source[ATTRIBUTE_GROUPS_FILTER_KEY];
    return Array.isArray(groups) && groups.length > 0
      ? 'grouped'
      : 'individual';
  };

  // Derive the toggle's initial state from the loaded filter so saved views
  // restore in the correct mode.
  const [attributeMode, setAttributeMode] = useState<AttributeMode>(() =>
    getModeFromFilters(listFilters)
  );

  useEffect(() => {
    setFilters({ ...listFilters, name: '' });
  }, []);

  // When a saved view is loaded the whole filters object is replaced. Restore
  // the toggle to the mode implied by the loaded grouped selection. Tracked via
  // a ref so manual mode switches (which clear the other mode) are not undone.
  const prevGroupedRef = useRef<string | undefined>();
  useEffect(() => {
    const grouped = filters?.[ATTRIBUTE_GROUPS_FILTER_KEY];
    const groupedKey = JSON.stringify(grouped ?? null);
    if (groupedKey !== prevGroupedRef.current) {
      prevGroupedRef.current = groupedKey;
      if (Array.isArray(grouped) && grouped.length > 0) {
        setAttributeMode('grouped');
      } else if (filters?.[ATTRIBUTE_MODE_KEY]) {
        setAttributeMode(filters[ATTRIBUTE_MODE_KEY] as AttributeMode);
      }
    }
  }, [filters]);

  // Switch attribute mode and clear the inactive mode's selections so the
  // derived request carries either `resourceAttributes` OR
  // `resourceAttributeGroupIds`, never both (mutual exclusivity).
  const handleAttributeModeChange = (nextMode: AttributeMode) => {
    if (nextMode === attributeMode) return;
    setAttributeMode(nextMode);
    setFilters((prev: ResourcesListFilter | undefined) => {
      const next = { ...(prev || {}) } as ResourcesListFilter;
      if (nextMode === 'grouped') {
        // Clear individual-attribute selections (keyed by display name).
        companyAttributes?.forEach((attribute) => {
          delete next[attribute.title];
        });
      } else {
        delete next[ATTRIBUTE_GROUPS_FILTER_KEY];
      }
      next[ATTRIBUTE_MODE_KEY] = nextMode;
      return next;
    });
  };

  const getQuickFilteValue = (filters: ResourcesListFilter): string => {
    if (!filters) return 'all';

    if (filters.isWorkTeam) {
      return 'isworkteam';
    }
    if (filters.isActive === true) {
      return 'active';
    }
    if (filters.isActive === false) {
      return 'inactive';
    }

    return 'all';
  };

  const columns: GridColDef[] = [
    boldLinkColumn<Resource, string>(
      {
        field: 'name',
        headerName: 'Name',
      },
      (resource, _rowValue) => `/resources/${resource?.integrationGUID}?tab=0`
    ),
    textColumn({
      field: 'integrationGUID',
      headerName: 'ID',
      sortable: false,
    }),
    textColumn({
      field: 'role',
      headerName: 'Role',
      valueGetter: (params) =>
        params.row?.company?.role
          ?.filter((c: CompanyRole) => {
            return params.row?.roles?.some(
              (r: { integrationGUID: string }) =>
                r?.integrationGUID?.toLowerCase() ===
                c?.integrationGUID?.toLowerCase()
            );
          })
          .map((role: CompanyRole) => role?.name)
          .join(' '),
      sortable: false,
    }),
    textColumn({
      field: 'team',
      headerName: 'Teams',
      valueGetter: (params) => {
        return params.row?.company?.team?.find((c: Team) => {
          return params.row?.teams?.some(
            (t: { integrationGUID: string }) =>
              t?.integrationGUID?.toLowerCase() ===
              c?.integrationGUID?.toLowerCase()
          );
        })?.name;
      },
      sortable: false,
    }),
    textColumn({
      field: 'state',
      headerName: 'State',
      valueGetter: (params) => params?.row?.locations[0]?.state,
    }),
    activeColumn({
      field: 'isActive',
      headerName: 'Active',
      sortable: false,
    }),
    activeColumn({
      field: 'isWorkTeam',
      headerName: 'Work Team',
      valueGetter: (params) => params?.row?.isWorkTeam,
    }),
  ];

  const tableArgs: TableProps<Resource> = {
    tableState: {
      columnModel: tableState['resources'].columnModel,
      pinnedColumns: {
        ...tableState['resources'].pinnedColumns,
      },
    },
    handleShowColumns: (newModel) => {
      if (!newModel) return;
      setTableState({
        ...tableState,
        resources: { ...tableState['resources'], columnModel: newModel },
      });
    },
    handlePinnedColumns: (newPinned) => {
      setTableState({
        ...tableState,
        resources: { ...tableState['resources'], pinnedColumns: newPinned },
      });
    },
    rows: data?.data || [],
    columns,
    height: 400,
    emptyEntityName: 'Resource',
    status: data?.status,
    getRowId: (row: Resource) => row.integrationGUID,
    serverPaging: {
      ...data,
      onPageSizeChange: (newPageSize: number) => {
        onFilterChange({ ...listFilters, pageSize: newPageSize });
      },
    },
    handleSort,
  };

  const renderActions = () => {
    return (
      permissions?.can?.('upsert', 'resource') && (
        <>
          <Button
            variant={'contained'}
            style={{ backgroundColor: '#2196F3' }}
            onClick={() => navigate('/resources/update-exclusion-zones')}
            sx={{ fontWeight: '700' }}
          >
            Bulk Update
          </Button>
          <CreateNewEntityButton
            onClick={() => navigate('/resources/add')}
            entityName="Resource"
          />
        </>
      )
    );
  };

  const companyAttributeFilters: Filter[] = useMemo(() => {
    return (
      companyAttributes?.map((companyAttribute) => {
        const filter: Filter = {
          id: companyAttribute.title,
          name: companyAttribute.title,
          label: `Resource ${companyAttribute.title}`,
          multi: true,
          type: 'select',
          options:
            companyAttribute.attributeDefinitionValues as MultiSelectOption[],
        };

        return filter;
      }) || []
    );
  }, [companyAttributes]);

  // Single multi-select dimension for grouped attributes. Its selection lands in
  // `filters[ATTRIBUTE_GROUPS_FILTER_KEY]` (a `string[]` of group IDs) and is
  // persisted as a native array so it round-trips through saved views.
  const groupedAttributeFilter: Filter = useMemo(
    () => ({
      id: ATTRIBUTE_GROUPS_FILTER_KEY,
      name: ATTRIBUTE_GROUPS_FILTER_KEY,
      label: 'Attribute Group',
      multi: true,
      type: 'select',
      persistAsArray: true,
      options: attributeGroupOptions,
    }),
    [attributeGroupOptions]
  );

  // Mutual exclusivity: surface only the active mode's attribute dimension so
  // the request carries exactly one attribute filter type.
  const attributeFilters: Filter[] =
    attributeMode === 'grouped'
      ? [groupedAttributeFilter]
      : companyAttributeFilters;

  const resourceFilterOptions: Filter[] = [
    {
      type: 'toggle',
      id: 'isStandby',
      name: 'isStandby',
      label: 'Standby Status',
      chipLabel: [
        {
          label: 'Standby',
          value: 'true',
        },
        {
          label: 'Active',
          value: 'false',
        },
      ],
      buttons: [
        {
          label: 'Standby',
          value: true,
        },
        {
          label: 'Active',
          value: false,
        },
      ],
    },
    {
      id: 'resourceStates',
      name: 'resourceStates',
      label: 'State',
      type: 'select',
      multi: true,
      onChange: (value: any) => setRsrcStateFilterInput(value),
      options: filterOptions['states'],
    },
    {
      id: 'resourceCounties',
      name: 'resourceCounties',
      label: 'Counties',
      type: 'async-select',
      multi: true,
      setInput: setRsrcCountyFilterInput,
      options: filteredRsrcCounties,
      loading: filteredRsrcCountiesStatus === 'loading',
    },
    {
      id: 'teamIds',
      name: 'teamIds',
      label: 'Teams',
      type: 'select',
      multi: true,
      options: filterOptions['teams'],
    },
    {
      id: 'groupIds',
      name: 'groupIds',
      label: 'Groups',
      type: 'select',
      multi: true,
      options: filterOptions['groups'],
    },
    {
      id: 'officeIds',
      name: 'officeIds',
      label: 'Offices',
      type: 'select',
      multi: true,
      options: filterOptions['offices'],
    },
    {
      id: 'roleIds',
      name: 'roleIds',
      label: 'Roles',
      type: 'select',
      multi: true,
      options: filterOptions['roles'],
    },
    {
      id: 'employmentType',
      name: 'employmentType',
      label: 'Employment Type',
      type: 'select',
      conditionalRender: {
        parentEquals: 'contingent',
        id: 'contingentCompanies',
        name: 'contingentCompanies',
        label: 'Company',
        type: 'select',
        multi: true,
        required: true,
        options: [
          {
            value: 'pilot',
            title: 'Pilot',
          },
          {
            value: 'crawford',
            title: 'Crawford',
          },
          {
            value: 'other',
            title: 'Other',
          },
        ],
      },
      options: [
        {
          value: 'staff',
          title: 'Staff',
        },
        {
          value: 'contingent',
          title: 'Contingent',
        },
      ],
    },
    ...attributeFilters,
  ];

  const getIsContingent = (employmentType?: string) => {
    if (employmentType === 'contingent') {
      return true;
    } else if (employmentType === 'staff') {
      return false;
    }
    return null;
  };

  useEffect(() => {
    const resourceAttributeFilters = companyAttributeFilters
      .filter((attribute) => {
        return filters?.[attribute.name];
      })
      .map((attribute) => {
        if (filters?.[attribute.name]) {
          return {
            attributeDefinition: attribute.name,
            values: filters?.[attribute.name],
          };
        }
      });

    // Derive the grouped-attribute API fields from the reserved filter key.
    // Kept derived (mirrors how `resourceAttributes` is built) so the AND
    // semantics are enforced server-side and the selection round-trips through
    // saved views under its `name` key rather than the derived API fields.
    const selectedAttributeGroupIds = filters?.[ATTRIBUTE_GROUPS_FILTER_KEY] as
      | string[]
      | undefined;
    const hasGroupedSelection =
      Array.isArray(selectedAttributeGroupIds) &&
      selectedAttributeGroupIds.length > 0;

    const filterValue = {
      ...filters,
      pageSize: listFilters.pageSize,
      resourceAttributes: resourceAttributeFilters,
      resourceAttributeGroupIds: hasGroupedSelection
        ? selectedAttributeGroupIds
        : undefined,
      requireAllAttributeGroups: hasGroupedSelection ? true : undefined,
      isActive:
        quickFilters?.isActive === 'all' ||
        quickFilters?.isActive === 'isworkteam'
          ? undefined
          : quickFilters?.isActive === 'active',
      isWorkTeam: quickFilters?.isActive === 'isworkteam' ? true : undefined,
      isContingent: getIsContingent(filters?.employmentType),
    };

    onFilterChange(filterValue);
    data?.goToFirstPage();
  }, [filters, quickFilters]);

  // Attribute-mode toggle. Rendered inside the filter modal directly after the
  // Employment Type filter (via the modal's `renderAfter` slot) so the
  // dispatcher switches mode in the natural flow of the filters, immediately
  // above the active mode's attribute dimension. Presentation mirrors the
  // "Group Attributes" Switch on the resource-detail blade's Attributes tab
  // (AttributesTab.tsx) so the control looks and reads identically in both
  // places: checked => grouped mode, unchecked => individual mode.
  const attributeModeToggle = (
    <FormControlLabel
      data-testid="attribute-mode-section"
      control={
        <Switch
          data-testid="attribute-mode-toggle"
          checked={attributeMode === 'grouped'}
          onChange={(e) =>
            handleAttributeModeChange(
              e.target.checked ? 'grouped' : 'individual'
            )
          }
          color="primary"
        />
      }
      label="Group Attributes"
      labelPlacement="start"
    />
  );

  const dynamicFilters: DynamicFilters<ResourcesListFilter> = {
    filters: [{ label: 'Resource', data: resourceFilterOptions }],
    allowSaveFilters: true,
    handleDeleteFilter: handleDeleteFilter,
    handleSaveFilters: handleSaveFilters,
    handleOrderIndexBulkUpdate,
    handleGroupsOrderIndexBulkUpdate,
    handleSaveFilterGroup: handleSaveFilterGroup,
    handleDeleteFilterGroup: handleDeleteFilterGroup,
    multi: true,
    storedFilters: storedFilters,
    // Inject the attribute-mode toggle directly after the Employment Type
    // filter (keyed by its filter `id`) so it renders in the natural flow of
    // the filter list rather than floating at the top of the modal.
    renderAfter: { employmentType: attributeModeToggle },
  };

  return (
    <PageComponent<ResourcesListFilter>
      searchPlaceholder="Name or ID"
      onSearch={onSearch}
      quickFilters={{
        filterKey: 'isActive',
        defaultValue: getQuickFilteValue(listFilters),
        filters: [
          { label: 'All', value: 'all' },
          { label: 'Active', value: 'active' },
          { label: 'Inactive', value: 'inactive' },
          { label: 'Work Team', value: 'isworkteam' },
        ],
      }}
      dynamicFilters={dynamicFilters}
      title="Resources"
      actions={renderActions()}
    >
      <StyledResources>
        <TableComponent<Resource> {...tableArgs} />
      </StyledResources>
    </PageComponent>
  );
}

export default ResourcesList;
